import React, { FC, useMemo, memo } from "react";
import { Modal, Table, Image, Spin, Empty } from "antd";
import { useGetSpeedAnalysisDetailQuery } from "shared/api";
import { useSpeedAnalysisColumns } from "shared/hooks/useSpeedAnalysisColumns";

interface SpeedAnalysisModalProps {
  visible: boolean;
  onClose: () => void;
  analysisId: number | null;
}

export const SpeedAnalysisModal: FC<SpeedAnalysisModalProps> = memo(
  ({ visible, onClose, analysisId }) => {
    const { data: speedAnalysis, isFetching } = useGetSpeedAnalysisDetailQuery(
      analysisId!,
      {
        skip: !analysisId || !visible,
      }
    );

    const columns = useSpeedAnalysisColumns();

    const tableData = useMemo(
      () => (speedAnalysis ? [{ ...speedAnalysis, key: "1" }] : []),
      [speedAnalysis]
    );

    return (
      <Modal
        title="Speed Analysis Detail"
        open={visible}
        onCancel={onClose}
        footer={null}
        width={1000}
        centered
        destroyOnClose
      >
        {isFetching && (
          <div className="flex justify-center items-center py-12">
            <Spin size="large" tip="Loading analysis..." />
          </div>
        )}

        {!isFetching && speedAnalysis && (
          <div className="space-y-6">
            {speedAnalysis.text && <span>{speedAnalysis.text}</span>}

            {speedAnalysis.image_base64 && (
              <div className="flex justify-center bg-gray-50 rounded-lg p-4">
                <Image
                  src={`data:image/png;base64,${speedAnalysis.image_base64}`}
                  alt="Speed Analysis Chart"
                  className="rounded shadow-sm"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "400px",
                    objectFit: "contain",
                  }}
                />
              </div>
            )}

            <Table
              columns={columns}
              dataSource={tableData}
              pagination={false}
              size="middle"
              bordered
              scroll={{ x: "max-content" }}
            />
          </div>
        )}

        {!isFetching && !speedAnalysis && (
          <Empty description="No data available" className="py-8" />
        )}
      </Modal>
    );
  }
);

SpeedAnalysisModal.displayName = "SpeedAnalysisModal";
